import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Component, Input, SimpleChanges, EventEmitter, Output, TemplateRef } from '@angular/core';
import { BasicDialogComponent } from './../../../shared/components/basic-dialog/basic-dialog.component';

import { DialogSearchComponent } from './../../../shared/components/dialog-search/dialog-search.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SaveService } from '@modules/policy/services/shared/save.service';
import { ClientService } from './../../../services/client/client.service';
import { Observable, of } from 'rxjs';
import { FormComponent } from '@modules/policy/shared/components/form/form.component';
import { ClientSearchOptions } from '@shared/models/client-search-options.model';
import { PolicyDataService } from '@modules/policy/services/shared/policy-data.service';
import { DriverManagerService } from '@modules/policy/services/driver/driver-manager.service';
import {
  CANCELLATION_EDITABLE_FIELDS,
  CANCELLATION_TRANSACTION_TYPE
} from '@modules/policy/shared/helpers/cancellation';

@Component({
  selector: 'app-client-search',
  templateUrl: './client-search.component.html',
  styleUrls: ['./client-search.component.scss']
})
export class ClientSearchComponent {
  @Output() clientKeySelection = new EventEmitter<string>();
  @Input() clientForm: FormGroup;
  @Input() tabType: string;
  @Input() subtabType: FormGroup;
  @Input() nodeId: string;
  @Input() options: ClientSearchOptions;
  @Input() formComponent: FormComponent;
  @Input() clientKey: string;
  @Input() policyId: number;
  @Input() customerNumIsEmpty?: boolean;
  @Input() addAsDriverEnabled?: boolean;
  @Input() rightActions?: TemplateRef<any>;

  private tab: string;
  private subtab: string;
  private searchResult: FormGroup;

  readonly BASE_OPTIONS: ClientSearchOptions = {
    sectionTitle: 'Policy Holder Search',
    objectName: 'client',
    clientReturnKeys: {
      clientKey: 'ClientPASClientId',
      contactReturnKey: 'ClientPASClientId',
      addressKey: 'ClientAddressPostalTxt',
      nameKey: 'ClientClientSearchCd',
    },
    formFields: {
      formName: 'ClientContactInformation',
      firstNameField: 'ClientFirstNameTxt',
      lastNameField: 'ClientLastNameTxt',
      companyNameField: 'ClientCompanyNameTxt',
      postalCodeField: 'ClientAddressPostalTxt'
    },
    service: this.ClientService
  }

  readonly EXCLUDED_CLIENT_SEARCH_FIELDS: { [s: string]: string[] } = {
    'client': ['searchResult'],
    'interest': ['searchResult'],
    'policy': ['searchResult'],
    'driver': ['searchResult']
  }

  constructor(
    public dialog: MatDialog,
    private ClientService: ClientService,
    private SaveService: SaveService,
    private snackbar: MatSnackBar,
    public policyDataService: PolicyDataService,
    private driverManagerService: DriverManagerService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.tabType && changes.subtabType) {
      this.initForm(changes.tabType.currentValue, changes.subtabType.currentValue);
    } else if (changes.clientForm) {
      this.initForm(this.tab, this.subtab);
    }
    if (changes.addAsDriverEnabled || changes.clientForm || changes.clientKey) {
      this.updateCompanyNameDisabledForSgi();
    }
  }

  initForm(tab, subtab) {
    this.tab = tab;
    this.subtab = subtab;
    this.options = Object.assign({}, this.BASE_OPTIONS, this.options);
    const contactForm = this.getContactInfoForm();
    if (contactForm != null) {
      this.searchResult = contactForm.get('searchResult') as FormGroup;
      if (this.searchResult == null) {
        this.searchResult = new FormGroup({
          client: new FormControl('', [Validators.required])
        });
        contactForm.addControl('searchResult', this.searchResult);
      }
    }
    if (this.clientKey != null) {
      this.disableContactInfoFields();
      this.displayClientSearchResult();
      this.searchResult?.disable();
    }
    this.updateCompanyNameDisabledForSgi();
  }

  /** SGI/SK only: disable company name when "Add this contact as a driver" is checked or client/driver already selected */
  private updateCompanyNameDisabledForSgi(): void {
    if (!this.policyDataService.sgiEnabled) return;
    const contactForm = this.getContactInfoForm();
    const companyNameField = this.options?.formFields?.companyNameField;
    if (!contactForm || !companyNameField) return;
    const companyNameControl = contactForm.get(companyNameField);
    if (!companyNameControl) return;
    const shouldDisable = this.addAsDriverEnabled || this.clientKey != null;
    if (shouldDisable) {
      if (this.addAsDriverEnabled) companyNameControl.setValue(null);
      companyNameControl.disable();
    } else {
      companyNameControl.enable();
    }
  }

  get isCancellation(): boolean {
    return this.policyDataService.getTransactionType() === CANCELLATION_TRANSACTION_TYPE;
  }

  searchDisabled(): boolean {
    const contactInfoForm = this.getContactInfoForm();
    if (!this.clientForm || this.clientKey != null || !contactInfoForm) {
      return true;
    }

    // Only check customerNumIsEmpty if on driver tab OR addAsDriver is enabled
    if (this.customerNumIsEmpty !== undefined &&(this.tabType === 'driver' || this.addAsDriverEnabled) && this.customerNumIsEmpty === true) {
      return true;
    }
    if (this.clientForm?.controls?.LicenseDetails?.status === "INVALID") {
      return true;
    }

    const excludedClientInfoFields = this.EXCLUDED_CLIENT_SEARCH_FIELDS[this.tab] || [];

    for (let field in contactInfoForm.controls) {
      if (!excludedClientInfoFields.includes(field)) {
        const formField = contactInfoForm.get(field);
        if (formField.status !== 'VALID' && formField.status !== 'DISABLED') {
          return true;
        }
      }
    }
    return false;
  }

  onSearch(): void {
    if (this.searchDisabled()) return;
    this.fetchClients().subscribe(clients => {
      const { Error } = clients;
      if (Error) this.openErrorDialog(Error);
      else this.openSearchModal(clients);
    });
  }

  disableContactInfoFields(): void {
    const contactInfoForm = this.getContactInfoForm();
    const excludedClientInfoFields = this.EXCLUDED_CLIENT_SEARCH_FIELDS[this.tab];
    const editableFields = this.isCancellation ? CANCELLATION_EDITABLE_FIELDS : [];

    for (let formConfig of this.formComponent.formjson) {
      for (let fieldConfig of formConfig.parameters) {
        if (!excludedClientInfoFields.includes(fieldConfig.name)) {
          fieldConfig.disabling = null;
        }
      }
    }

    for (let field in contactInfoForm.controls) {
      if (!excludedClientInfoFields.includes(field) && !editableFields.includes(field)) {
        contactInfoForm.get(field).disable();
      } else if (editableFields.includes(field)) {
        contactInfoForm.get(field).enable();
      }
    }
  }

  getContactFormMessage(): string {
    if (this.tab === 'driver') {
      if (this.policyDataService.sgiEnabled) {
        return 'Please fill out the contact form and customer number above, then click the search button below to finish filling out the form.';
      } else {
        return 'Please fill out the contact form and license number above, then click the search button below to finish filling out the form.'
      }
    }
    return 'Please fill out the contact form above, then click the search button below to finish filling out the form.';
  }

  getContactInfoForm(): FormGroup {
    if (!this.clientForm) {
      return null;
    }
    const primaryName = this.options.formFields.formName;
    let form = this.clientForm.controls[primaryName] as FormGroup;
    // HAB configs use ClientPrimaryInsuredContactInformation; Auto uses ClientContactInformation.
    // QA may serve Auto configs or a patched HAB config; dev/prod HAB use the H form name.
    if (!form && primaryName === 'ClientContactInformation') {
      form = this.clientForm.controls['ClientPrimaryInsuredContactInformation'] as FormGroup;
    }
    return form || null;
  }

  getContactInfoFormRawValue(): { [s: string]: any } {
    const res = {};
    const clientFormRawValues = this.getContactInfoForm().getRawValue();
    const excludedClientSearchFields = this.EXCLUDED_CLIENT_SEARCH_FIELDS[this.tab] || [];

    for (let field in clientFormRawValues) {
      if (!excludedClientSearchFields.includes(field)) {
        res[field] = clientFormRawValues[field];
      }
    }
    return res;
  }

  displayClientSearchResult() {
          
    const clientField = this.searchResult.get('client');
    clientField.disable();
    let clientValue = '';

    const formRawValues = this.getContactInfoFormRawValue();
    const firstName: string = formRawValues[this.options.formFields.firstNameField];
    const lastName: string = formRawValues[this.options.formFields.lastNameField];
    const companyName: string = formRawValues[this.options.formFields.companyNameField];
    const postalTxt: string = formRawValues[this.options.formFields.postalCodeField];

    if (companyName) {
      clientValue += `${companyName} - ${postalTxt}`;
    } else {
      clientValue += `${firstName} ${lastName} - ${postalTxt}`;
    }

    clientField.setValue(clientValue);
  }

  fetchClients(): Observable<any> {
    const formRawValues = this.getContactInfoFormRawValue();
    const firstName: string = formRawValues[this.options.formFields.firstNameField];
    const lastName: string = formRawValues[this.options.formFields.lastNameField];
    const companyName: string = formRawValues[this.options.formFields.companyNameField];
    const postalTxt: string = formRawValues[this.options.formFields.postalCodeField];

    if (companyName && postalTxt) {
      return this.options.service.getClientsByZip(companyName, postalTxt, this.policyId);
    } else if (firstName && lastName && postalTxt) {
      return this.options.service.getClientsByName(firstName, lastName, postalTxt, this.policyId);
    } else {
      return of([]);
    }
  }

  openSearchModal(clients) {
    this.formComponent.saveBeforeDeactivate();
    const data = {
      clients,
      title: this.options.sectionTitle,
      contactKey: this.options.clientReturnKeys.contactReturnKey,
      addressKey: this.options.clientReturnKeys.addressKey,
      nameKey: this.options.clientReturnKeys.nameKey,
      objectName: this.options.objectName
    }
    this.dialog.open(DialogSearchComponent, {
      data,
      width: '60%',
      maxWidth: '600px',
      minWidth: '480px'
    }).afterClosed().subscribe(formValues => {
      if (formValues == null || Object.keys(formValues).length === 0) return;
      const { id } = formValues;
      let body: { [s: string]: any } = {};
      body[this.options.clientReturnKeys.clientKey] = id;
      body = Object.assign(body, this.getContactInfoFormRawValue());
      
      
      this.SaveService.updatePolicy(this.policyId, body, this.nodeId).subscribe(
        (result) => {
          const { Error } = result;
          if (Error) {
            this.openErrorDialog(Error);
          } else {
            this.snackbar.open('Success', null, {
              duration: 2000,
            });
            this.displayClientSearchResult();
            this.formComponent.saveBeforeDeactivate(true).then(_ => {
              this.disableContactInfoFields();
              this.clientKeySelection.emit(result.data[this.options.clientReturnKeys.clientKey]);
              
              // TRIGGER DRIVER ADDITION IF THIS IS A DRIVER CONTEXT
              console.log('Client search completed - triggering driver addition');
              
              // Only add as driver if checkbox is checked
              const addAsDriverChecked = this.clientForm?.get('LicenseDetails')?.get('AddContactAsDriverInd')?.value;
              if (this.tabType === 'driver') {
                return;
              }
              // Check both LicenseDetails checkbox (MB) and addAsDriverEnabled input (SGI)
              if (!addAsDriverChecked && !this.addAsDriverEnabled) {
                return;
              }
              this.driverManagerService.addDriver(this.nodeId, this.tabType).subscribe({
                next: (response) => {
                  this.snackbar.open('Driver added successfully', null, { duration: 2000 });
                },
                error: (error) => {
                  this.snackbar.open('Failed to add driver', null, { duration: 2000 });
                }
              });
              
            })
          }
        });
        
    });
  }

  openErrorDialog(error) {
    this.dialog.open(BasicDialogComponent, {
      data: {
        message: error.Message,
        details: error.Details,
        onlyConfirm: true,
        title: 'Error',
        showDetails: true,
        dialogPrefix: 'client-search-error-modal'
      },
      width: '80%',
      maxWidth: '600px'
    });
  }
}
